/**
 * MAQUETA — Flujo de Check-in Legal de documentos para evento.
 *
 * Ruta pública: /legalchekintest
 *
 * Esta página replica la estructura visual del componente real
 * `ConsentCheckinModal.tsx` (paso a paso, mismos textos, mismos
 * componentes UI) PERO sin escribir a Firestore ni Storage:
 *  - Las "subidas" de imagen muestran el preview localmente y avanzan
 *  - Los formularios no validan
 *  - La firma usa el componente real `SignaturePad`
 *  - El "PDF" se muestra como una vista previa estática del texto legal
 *
 * Sirve para socializar con la directiva el flujo completo y los textos
 * legales que firmará cada participante (o sus padres si es menor).
 */

import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Upload,
  Camera,
  FileText,
  Eye,
  Pencil,
  Info,
  ShieldCheck,
  Lock,
  ClipboardSignature,
  Users,
  User,
  Clock,
  RefreshCw,
} from "lucide-react";

import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Select from "@/components/ui/Select";
import SignaturePad from "@/components/consent/SignaturePad";
import ParentPhotoCapture from "@/components/consent/ParentPhotoCapture";

type Audience = "minor" | "adult";
type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7;
type Step6Substep = "form" | "preview" | "sign";
type IdDocumentType = "CC" | "CE" | "PASAPORTE" | "TI" | "RC";

interface ChecklistItem {
  id: string;
  label: string;
  shortLabel: string;
  done: boolean;
}

interface ParentDraft {
  fullName: string;
  idDocumentType: IdDocumentType;
  idDocumentNumber: string;
  address: string;
  relationship: "padre" | "madre" | "acudiente";
  signatureDataUrl?: string;
  photoFile?: File;
  photoPreviewUrl?: string;
}

const emptyParent = (): ParentDraft => ({
  fullName: "",
  idDocumentType: "CC",
  idDocumentNumber: "",
  address: "",
  relationship: "padre",
});

// Datos ficticios del "evento" para la maqueta
const MOCK_EVENT_NAME = "Campamento Estrellitas 2026";
const MOCK_ASSOCIATION = "Asociación del Oriente Colombiano";
const MOCK_CLUB = "Águilas Blancas";

export default function LegalCheckinTestPage() {
  const [audience, setAudience] = useState<Audience>("minor");
  const [step, setStep] = useState<Step>(1);
  const [step6Substep, setStep6Substep] = useState<Step6Substep>("form");

  // Estado por paso (idéntico al modal real)
  const [previewUrlFront, setPreviewUrlFront] = useState<string | null>(null);
  const [previewUrlBack, setPreviewUrlBack] = useState<string | null>(null);
  const [previewUrlEps, setPreviewUrlEps] = useState<string | null>(null);

  const [medicalDraft, setMedicalDraft] = useState({
    healthInsurance: "",
    noMedicalConditions: false,
    medicalConditions: "",
    allergies: "",
    medications: "",
    departureCity: "",
    returnCity: "",
    idExpeditionCity: "",
  });

  const [dataConsentSignature, setDataConsentSignature] = useState<
    string | null
  >(null);
  const [dataConsentAccepted, setDataConsentAccepted] = useState(false);

  const [participantSignature, setParticipantSig] = useState<string | null>(
    null,
  );
  const [parents, setParents] = useState<ParentDraft[]>([emptyParent()]);

  const [participantPhotoPreview, setParticipantPhotoPreview] = useState<
    string | null
  >(null);
  const [completed, setCompleted] = useState(false);

  // refs para inputs de archivo
  const fileFront = useRef<HTMLInputElement>(null);
  const fileBack = useRef<HTMLInputElement>(null);
  const fileEps = useRef<HTMLInputElement>(null);

  const isMinor = audience === "minor";

  // Resetear todo al cambiar audiencia
  function switchAudience(a: Audience) {
    setAudience(a);
    setStep(1);
    setStep6Substep("form");
    setPreviewUrlFront(null);
    setPreviewUrlBack(null);
    setPreviewUrlEps(null);
    setDataConsentSignature(null);
    setDataConsentAccepted(false);
    setParticipantSig(null);
    setParents([emptyParent()]);
    setParticipantPhotoPreview(null);
    setCompleted(false);
  }

  function handleFile(setUrl: (v: string | null) => void) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (f) setUrl(URL.createObjectURL(f));
      e.target.value = "";
    };
  }

  function updateParent(idx: number, patch: Partial<ParentDraft>) {
    setParents((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    );
  }
  function addParent() {
    setParents((prev) => (prev.length < 2 ? [...prev, emptyParent()] : prev));
  }
  function removeParent(idx: number) {
    setParents((prev) => prev.filter((_, i) => i !== idx));
  }

  // Checklist visual (coincide con buildChecklist real)
  const checklist: ChecklistItem[] = useMemo(
    () => [
      {
        id: "id-front",
        label: "Documento — Anverso",
        shortLabel: "Documento (anverso)",
        done: !!previewUrlFront,
      },
      {
        id: "id-back",
        label: "Documento — Reverso",
        shortLabel: "Documento (reverso)",
        done: !!previewUrlBack,
      },
      {
        id: "eps",
        label: "Certificado EPS",
        shortLabel: "Certificado EPS",
        done: !!previewUrlEps,
      },
      {
        id: "medical",
        label: "Datos médicos y de viaje",
        shortLabel: "Datos médicos y viaje",
        done: !!medicalDraft.healthInsurance,
      },
      {
        id: "data-consent",
        label: "Tratamiento de datos",
        shortLabel: "Tratamiento de datos",
        done: dataConsentAccepted,
      },
      {
        id: "auth",
        label: "Autorización de participación",
        shortLabel: "Autorización de participación",
        done: completed,
      },
      {
        id: "photo",
        label: "Foto del participante",
        shortLabel: "Foto del participante",
        done: !!participantPhotoPreview,
      },
    ],
    [
      previewUrlFront,
      previewUrlBack,
      previewUrlEps,
      medicalDraft.healthInsurance,
      dataConsentAccepted,
      completed,
      participantPhotoPreview,
    ],
  );

  const stepShortLabel = checklist[step - 1]?.shortLabel ?? "";

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 pb-16">
      {/* HEADER */}
      <header className="border-b border-primary-100 bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 grid place-items-center shadow-lg">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-primary-900 leading-tight">
                Maqueta · Check-in Legal
              </h1>
              <p className="text-xs text-gray-500">
                Componentes reales · sin escritura en BD
              </p>
            </div>
          </div>
          <Link
            to="/"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium hidden sm:inline"
          >
            ← Volver al inicio
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-8">
        {/* INTRO */}
        <section className="text-center mb-8">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold uppercase tracking-wide mb-3">
            <Info className="w-3.5 h-3.5" />
            Maqueta · sin guardado real
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Vista previa del flujo de Check-in
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Esta maqueta usa los <strong>mismos componentes</strong> que el
            sistema en producción. Recorra los 7 pasos para ver exactamente lo
            que verá el participante (o el acudiente si es menor) antes de cada
            evento.
          </p>
        </section>

        {/* SELECTOR DE AUDIENCIA */}
        <section className="mb-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2 inline-flex mx-auto w-full max-w-md">
            <button
              onClick={() => switchAudience("minor")}
              className={`flex-1 px-4 py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                audience === "minor"
                  ? "bg-gradient-to-br from-primary-500 to-accent-500 text-white shadow-md"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Users className="w-4 h-4" />
              Menor de edad
            </button>
            <button
              onClick={() => switchAudience("adult")}
              className={`flex-1 px-4 py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                audience === "adult"
                  ? "bg-gradient-to-br from-primary-500 to-accent-500 text-white shadow-md"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <User className="w-4 h-4" />
              Mayor de edad
            </button>
          </div>
          <p className="text-center text-sm text-gray-500 mt-3">
            {isMinor
              ? "El acudiente firma la autorización y debe subir su propia foto y cédula."
              : "El propio participante firma todos los documentos."}
          </p>
        </section>

        {/* PANEL "MODAL" CON EL FLUJO REAL */}
        <section className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {/* Cabecera tipo modal */}
          <div className="bg-white border-b border-gray-100 px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-bold text-gray-900">
                Check-in de documentos · {MOCK_EVENT_NAME}
              </h3>
              <span className="text-xs text-gray-400">
                {audience === "minor" ? "Menor de edad" : "Mayor de edad"}
              </span>
            </div>
            <p className="text-xs text-gray-500">
              Vista del modal que aparece al participante después de la
              inscripción y el pago.
            </p>
          </div>

          <div className="p-4 sm:p-6">
            {/* STEPPER */}
            <div className="mb-5 sticky top-16 -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 px-4 sm:px-6 pt-3 pb-3 bg-white/95 backdrop-blur-sm z-10 border-b border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-primary-600 uppercase tracking-wide">
                  Paso {step} de {checklist.length}
                </span>
                <span className="text-xs text-gray-500">
                  {checklist.filter((c) => c.done).length}/{checklist.length}{" "}
                  completos
                </span>
              </div>
              <div className="flex gap-1.5 mb-2">
                {checklist.map((item, idx) => {
                  const isCurrent = step === idx + 1;
                  const isDone = item.done;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setStep((idx + 1) as Step)}
                      className="flex-1 group py-2 -my-2 touch-manipulation"
                      aria-label={`Ir al paso ${idx + 1}: ${item.label}`}
                    >
                      <span
                        className={`block h-2.5 rounded-full transition-all ${
                          isDone
                            ? "bg-green-500"
                            : isCurrent
                              ? "bg-primary-500 ring-2 ring-primary-200"
                              : "bg-gray-200 group-hover:bg-gray-300"
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
              <h4 className="text-base sm:text-lg font-bold text-gray-900 leading-tight">
                {stepShortLabel}
              </h4>
              <p className="mt-1 text-[11px] text-gray-500">
                Puedes cerrar y continuar más tarde — tu progreso se guarda
                automáticamente.
              </p>
            </div>

            {/* ===== PASO 1: ANVERSO ===== */}
            {step === 1 && (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Tome una foto clara del <strong>frente</strong> del documento
                  o escanéelo con la cámara.
                </p>
                <DocumentSlot
                  previewUrl={previewUrlFront}
                  placeholder="Sin documento"
                />
                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => fileFront.current?.click()}
                    className="flex-1 justify-center py-3"
                  >
                    <Camera className="w-5 h-5 mr-2" /> Tomar foto
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileFront.current?.click()}
                    className="flex-1 justify-center py-3"
                  >
                    <Upload className="w-5 h-5 mr-2" />{" "}
                    {previewUrlFront ? "Reemplazar" : "Subir archivo"}
                  </Button>
                </div>
                <input
                  ref={fileFront}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={handleFile(setPreviewUrlFront)}
                />
                {previewUrlFront && (
                  <Button
                    type="button"
                    variant="primary"
                    className="mt-5 w-full py-3.5 text-base font-semibold"
                    onClick={() => setStep(2)}
                  >
                    Continuar →
                  </Button>
                )}
              </div>
            )}

            {/* ===== PASO 2: REVERSO ===== */}
            {step === 2 && (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Tome una foto clara del <strong>reverso</strong> (parte de
                  atrás) del documento.
                </p>
                <DocumentSlot
                  previewUrl={previewUrlBack}
                  placeholder="Sin documento"
                />
                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => fileBack.current?.click()}
                    className="flex-1 justify-center py-3"
                  >
                    <Camera className="w-5 h-5 mr-2" /> Tomar foto
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileBack.current?.click()}
                    className="flex-1 justify-center py-3"
                  >
                    <Upload className="w-5 h-5 mr-2" />{" "}
                    {previewUrlBack ? "Reemplazar" : "Subir archivo"}
                  </Button>
                </div>
                <input
                  ref={fileBack}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={handleFile(setPreviewUrlBack)}
                />
                {previewUrlBack && (
                  <Button
                    type="button"
                    variant="primary"
                    className="mt-5 w-full py-3.5 text-base font-semibold"
                    onClick={() => setStep(3)}
                  >
                    Continuar →
                  </Button>
                )}
              </div>
            )}

            {/* ===== PASO 3: EPS ===== */}
            {step === 3 && (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Suba el certificado vigente de afiliación a{" "}
                  <strong>EPS o Sisbén</strong>.
                </p>
                <DocumentSlot
                  previewUrl={previewUrlEps}
                  placeholder="Sin certificado"
                />
                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => fileEps.current?.click()}
                    className="flex-1 justify-center py-3"
                  >
                    <Camera className="w-5 h-5 mr-2" /> Tomar foto
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileEps.current?.click()}
                    className="flex-1 justify-center py-3"
                  >
                    <Upload className="w-5 h-5 mr-2" />{" "}
                    {previewUrlEps ? "Reemplazar" : "Subir archivo"}
                  </Button>
                </div>
                <input
                  ref={fileEps}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={handleFile(setPreviewUrlEps)}
                />
                {previewUrlEps && (
                  <Button
                    type="button"
                    variant="primary"
                    className="mt-5 w-full py-3.5 text-base font-semibold"
                    onClick={() => setStep(4)}
                  >
                    Continuar →
                  </Button>
                )}
              </div>
            )}

            {/* ===== PASO 4: DATOS MÉDICOS Y VIAJE ===== */}
            {step === 4 && (
              <div className="space-y-5">
                <p className="text-sm text-gray-600">
                  Confirme su información médica y los lugares de salida y
                  regreso. Estos datos se usarán en el documento oficial de
                  autorización.
                </p>

                <Input
                  label="EPS o entidad de salud"
                  value={medicalDraft.healthInsurance}
                  onChange={(e) =>
                    setMedicalDraft((d) => ({
                      ...d,
                      healthInsurance: e.target.value,
                    }))
                  }
                  placeholder="Ej: Sura, Sanitas, SISBEN..."
                  required
                />

                <div className="rounded-lg border border-gray-200 p-3 space-y-3 bg-gray-50">
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="mt-1 w-4 h-4 accent-primary-600"
                      checked={medicalDraft.noMedicalConditions}
                      onChange={(e) =>
                        setMedicalDraft((d) => ({
                          ...d,
                          noMedicalConditions: e.target.checked,
                        }))
                      }
                    />
                    <span className="text-sm text-gray-800">
                      <strong>No presento</strong> condiciones médicas, alergias
                      ni tomo medicamentos relevantes
                    </span>
                  </label>

                  {!medicalDraft.noMedicalConditions && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          Condiciones médicas / enfermedades
                        </label>
                        <textarea
                          rows={2}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          value={medicalDraft.medicalConditions}
                          onChange={(e) =>
                            setMedicalDraft((d) => ({
                              ...d,
                              medicalConditions: e.target.value,
                            }))
                          }
                          placeholder="Ej: asma, diabetes, hipertensión..."
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          Alergias
                        </label>
                        <textarea
                          rows={2}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          value={medicalDraft.allergies}
                          onChange={(e) =>
                            setMedicalDraft((d) => ({
                              ...d,
                              allergies: e.target.value,
                            }))
                          }
                          placeholder="Ej: penicilina, mariscos, nueces..."
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          Medicamentos permanentes
                        </label>
                        <textarea
                          rows={2}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          value={medicalDraft.medications}
                          onChange={(e) =>
                            setMedicalDraft((d) => ({
                              ...d,
                              medications: e.target.value,
                            }))
                          }
                          placeholder="Indique nombre, dosis y frecuencia"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    label="Ciudad de salida"
                    value={medicalDraft.departureCity}
                    onChange={(e) =>
                      setMedicalDraft((d) => ({
                        ...d,
                        departureCity: e.target.value,
                      }))
                    }
                    placeholder="Ej: Bogotá"
                    required
                  />
                  <Input
                    label="Ciudad de regreso"
                    value={medicalDraft.returnCity}
                    onChange={(e) =>
                      setMedicalDraft((d) => ({
                        ...d,
                        returnCity: e.target.value,
                      }))
                    }
                    placeholder="Ej: Bogotá"
                    required
                  />
                  {!isMinor && (
                    <Input
                      label="Lugar de expedición del documento"
                      value={medicalDraft.idExpeditionCity}
                      onChange={(e) =>
                        setMedicalDraft((d) => ({
                          ...d,
                          idExpeditionCity: e.target.value,
                        }))
                      }
                      placeholder="Ciudad donde se expidió su CC"
                      required
                      className="sm:col-span-2"
                    />
                  )}
                </div>

                <Button
                  type="button"
                  variant="primary"
                  className="w-full py-3.5 text-base font-semibold"
                  onClick={() => setStep(5)}
                >
                  Guardar y continuar →
                </Button>
              </div>
            )}

            {/* ===== PASO 5: HABEAS DATA ===== */}
            {step === 5 && (
              <div>
                <p className="text-sm text-gray-600 mb-3">
                  Lea y firme para aceptar el tratamiento de sus datos
                  personales.
                </p>
                <div className="text-xs text-gray-700 space-y-2 max-h-48 overflow-y-auto p-3 bg-gray-50 rounded-lg border">
                  <p>
                    Autorizo de manera previa, expresa, voluntaria e informada a
                    la Iglesia Adventista del Séptimo Día, a la Unión Colombiana
                    del Norte, a {MOCK_ASSOCIATION} y al club organizador, para
                    recolectar, almacenar, usar, circular y procesar mis datos
                    personales (incluyendo datos sensibles como información
                    médica) con la finalidad de: gestionar mi inscripción y
                    participación en el evento, contactar en caso de emergencia,
                    generar estadísticas internas, y emitir comunicaciones
                    institucionales.
                  </p>
                  <p>
                    El tratamiento se realiza conforme a la Ley 1581 de 2012, su
                    Decreto 1377 de 2013 y normas concordantes. Conozco que
                    asiste el derecho a conocer, actualizar, rectificar y
                    solicitar la supresión de mis datos en los términos legales.
                  </p>
                </div>
                {dataConsentAccepted ? (
                  <>
                    <div className="mt-3 flex items-center gap-2 text-green-700 text-sm">
                      <CheckCircle2 className="w-4 h-4" /> Aceptado y firmado
                    </div>
                    <Button
                      type="button"
                      variant="primary"
                      className="mt-5 w-full py-3.5 text-base font-semibold"
                      onClick={() => setStep(6)}
                    >
                      Continuar →
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="mt-4">
                      <SignaturePad
                        label="Firma de aceptación"
                        hint="Firme dentro del recuadro para aceptar el tratamiento de datos"
                        onConfirm={(d) => setDataConsentSignature(d)}
                        onClear={() => setDataConsentSignature(null)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="primary"
                      className="mt-5 w-full py-3.5 text-base font-semibold"
                      onClick={() => {
                        setDataConsentAccepted(true);
                        setStep(6);
                      }}
                      disabled={!dataConsentSignature}
                    >
                      Acepto el tratamiento de datos
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* ===== PASO 6 — ADULTO: form/preview/sign ===== */}
            {step === 6 && !isMinor && step6Substep === "form" && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  El sistema generará el documento de autorización con los datos
                  que ya nos has dado. En el siguiente paso podrás revisarlo
                  antes de firmar.
                </p>
                <Button
                  type="button"
                  variant="primary"
                  className="w-full py-3.5 text-base font-semibold"
                  onClick={() => setStep6Substep("preview")}
                >
                  Generar documento →
                </Button>
              </div>
            )}

            {step === 6 && !isMinor && step6Substep === "preview" && (
              <div className="space-y-4">
                <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-900">
                  <Eye className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <p className="text-xs">
                    <strong>Revise atentamente el documento</strong> que va a
                    firmar. Verifique sus datos personales, EPS, condiciones
                    médicas y ciudades. Si algo está mal, vuelva a editarlo
                    antes de firmar.
                  </p>
                </div>
                <PdfMockPreview audience="adult" />
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 py-3 text-base"
                    onClick={() => setStep(4)}
                  >
                    <Pencil className="w-4 h-4 mr-2" /> Editar mis datos
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    className="flex-1 py-3 text-base font-semibold"
                    onClick={() => setStep6Substep("sign")}
                  >
                    He revisado, firmar →
                  </Button>
                </div>
              </div>
            )}

            {step === 6 && !isMinor && step6Substep === "sign" && (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Firme dentro del recuadro. Al confirmar se guardará el
                  documento firmado.
                </p>
                <SignaturePad
                  label="Firma del participante"
                  hint="Firme dentro del recuadro y presione Confirmar"
                  onConfirm={(d) => setParticipantSig(d)}
                  onClear={() => setParticipantSig(null)}
                />
                <div className="flex flex-col sm:flex-row gap-2 mt-5">
                  <Button
                    type="button"
                    variant="outline"
                    className="sm:w-1/3 py-3 text-base"
                    onClick={() => setStep6Substep("preview")}
                  >
                    ← Volver al documento
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    className="flex-1 py-3.5 text-base font-semibold"
                    disabled={!participantSignature}
                    onClick={() => setStep(7)}
                  >
                    Confirmar y guardar
                  </Button>
                </div>
              </div>
            )}

            {/* ===== PASO 6 — MENOR: form/preview/sign ===== */}
            {step === 6 && isMinor && step6Substep === "form" && (
              <div className="space-y-6">
                <p className="text-sm text-gray-600">
                  Este formato debe ser diligenciado y firmado por el padre,
                  madre o acudiente legal del menor. Se requiere también una
                  foto de quien firma para verificación.
                </p>

                {parents.map((p, idx) => (
                  <div
                    key={idx}
                    className="border border-gray-200 rounded-lg p-3 sm:p-4 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-sm">
                        Padre/Madre/Acudiente {idx + 1}
                      </h4>
                      {parents.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          onClick={() => removeParent(idx)}
                        >
                          Quitar
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input
                        label="Nombre completo"
                        value={p.fullName}
                        onChange={(e) =>
                          updateParent(idx, { fullName: e.target.value })
                        }
                        required
                      />
                      <Select
                        label="Relación"
                        value={p.relationship}
                        onChange={(e) =>
                          updateParent(idx, {
                            relationship: e.target
                              .value as ParentDraft["relationship"],
                          })
                        }
                      >
                        <option value="padre">Padre</option>
                        <option value="madre">Madre</option>
                        <option value="acudiente">Acudiente legal</option>
                      </Select>
                      <Select
                        label="Tipo de documento"
                        value={p.idDocumentType}
                        onChange={(e) =>
                          updateParent(idx, {
                            idDocumentType: e.target.value as IdDocumentType,
                          })
                        }
                      >
                        <option value="CC">Cédula de ciudadanía</option>
                        <option value="CE">Cédula de extranjería</option>
                        <option value="PASAPORTE">Pasaporte</option>
                      </Select>
                      <Input
                        label="Número de documento"
                        value={p.idDocumentNumber}
                        onChange={(e) =>
                          updateParent(idx, {
                            idDocumentNumber: e.target.value,
                          })
                        }
                        required
                      />
                      <Input
                        label="Domicilio"
                        value={p.address}
                        onChange={(e) =>
                          updateParent(idx, { address: e.target.value })
                        }
                        required
                        className="sm:col-span-2"
                      />
                    </div>

                    <ParentPhotoCapture
                      defaultFacingMode="environment"
                      onCapture={(file, previewUrl) =>
                        updateParent(idx, {
                          photoFile: file,
                          photoPreviewUrl: previewUrl,
                        })
                      }
                      onClear={() =>
                        updateParent(idx, {
                          photoFile: undefined,
                          photoPreviewUrl: undefined,
                        })
                      }
                    />
                  </div>
                ))}

                {parents.length < 2 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addParent}
                  >
                    + Agregar segundo padre/acudiente
                  </Button>
                )}

                <Button
                  type="button"
                  variant="primary"
                  onClick={() => setStep6Substep("preview")}
                  className="w-full py-3.5 text-base font-semibold"
                >
                  Continuar → Ver el documento
                </Button>
              </div>
            )}

            {step === 6 && isMinor && step6Substep === "preview" && (
              <div className="space-y-4">
                <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-900">
                  <Eye className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <p className="text-xs">
                    <strong>Revisen atentamente el documento</strong> que van a
                    firmar. Verifiquen los datos del menor, los datos del/los
                    padre(s) o acudiente(s) y las condiciones médicas. Si algo
                    está mal, pueden volver a editarlo antes de firmar.
                  </p>
                </div>
                <PdfMockPreview audience="minor" />
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 py-3 text-base"
                      onClick={() => setStep(4)}
                    >
                      <Pencil className="w-4 h-4 mr-2" /> Editar datos médicos
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 py-3 text-base"
                      onClick={() => setStep6Substep("form")}
                    >
                      <Pencil className="w-4 h-4 mr-2" /> Editar datos del padre
                    </Button>
                  </div>
                  <Button
                    type="button"
                    variant="primary"
                    className="py-3 text-base font-semibold"
                    onClick={() => setStep6Substep("sign")}
                  >
                    He revisado, firmar →
                  </Button>
                </div>
              </div>
            )}

            {step === 6 && isMinor && step6Substep === "sign" && (
              <div className="space-y-6">
                <p className="text-sm text-gray-600">
                  Cada padre/acudiente debe firmar dentro del recuadro
                  correspondiente.
                </p>
                {parents.map((p, idx) => (
                  <div
                    key={idx}
                    className="border border-gray-200 rounded-lg p-4 space-y-2"
                  >
                    <h4 className="font-semibold text-sm">
                      Firma de {p.fullName || `padre/acudiente ${idx + 1}`}
                    </h4>
                    <SignaturePad
                      label="Firma"
                      initialDataUrl={p.signatureDataUrl}
                      onConfirm={(d) =>
                        updateParent(idx, { signatureDataUrl: d })
                      }
                      onClear={() =>
                        updateParent(idx, { signatureDataUrl: undefined })
                      }
                    />
                  </div>
                ))}
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="sm:w-1/3 py-3 text-base"
                    onClick={() => setStep6Substep("preview")}
                  >
                    ← Volver al documento
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    className="flex-1 py-3.5 text-base font-semibold"
                    onClick={() => setStep(7)}
                  >
                    Confirmar y guardar
                  </Button>
                </div>
              </div>
            )}

            {/* ===== PASO 7: FOTO DEL PARTICIPANTE ===== */}
            {step === 7 && (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Tome una foto clara <strong>de frente</strong> del
                  participante para verificación de identidad.
                </p>

                {participantPhotoPreview ? (
                  <div className="relative aspect-square max-w-[220px] mx-auto mb-3 rounded-full overflow-hidden border-4 border-blue-200 shadow">
                    <img
                      src={participantPhotoPreview}
                      alt="Foto del participante"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      className="absolute bottom-2 right-2 bg-white rounded-full p-1 shadow text-gray-600"
                      onClick={() => setParticipantPhotoPreview(null)}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <ParentPhotoCapture
                    label="Foto del participante"
                    hint="Use la cámara frontal. Asegúrese de que el rostro esté bien iluminado."
                    onCapture={(_file, previewUrl) =>
                      setParticipantPhotoPreview(previewUrl)
                    }
                    onClear={() => setParticipantPhotoPreview(null)}
                  />
                )}

                <Button
                  type="button"
                  variant="primary"
                  className="mt-5 w-full py-3.5 text-base font-semibold"
                  disabled={!participantPhotoPreview}
                  onClick={() => setCompleted(true)}
                >
                  Completar check-in
                </Button>
              </div>
            )}

            {completed && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">¡Check-in completado!</p>
                  <p className="text-sm">
                    Tu consentimiento ha sido firmado y registrado. (En la
                    maqueta no se guarda nada.)
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* GARANTÍAS LEGALES */}
        <section className="mt-10 bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary-600" />
            Garantías legales y de seguridad
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Guarantee
              icon={Lock}
              title="Cifrado en cliente"
              text="Las cédulas y EPS se cifran en el navegador (AES-256-GCM) antes de subir. La clave se guarda separada del archivo."
            />
            <Guarantee
              icon={ShieldCheck}
              title="Ley 1581 / Habeas Data"
              text="Cumplimos la política de tratamiento de datos personales con firma explícita por participante o acudiente."
            />
            <Guarantee
              icon={ClipboardSignature}
              title="Firma con valor legal"
              text="Firma manuscrita digital + hash + timestamp del servidor, registrados en el consent."
            />
            <Guarantee
              icon={Users}
              title="Acceso restringido"
              text="Solo el participante (o su acudiente vinculado) y el director de su club ven los documentos."
            />
            <Guarantee
              icon={FileText}
              title="PDF auditable"
              text="El permiso de viaje firmado se archiva en PDF con metadata: firmante, fecha y datos del evento."
            />
            <Guarantee
              icon={Clock}
              title="Vigencia por evento"
              text="Cada consentimiento se vincula a un evento específico. No se reutiliza para otro campamento."
            />
          </div>
        </section>

        <footer className="mt-10 text-center text-xs text-gray-400">
          Maqueta · sin escritura a base de datos · /legalchekintest ·{" "}
          {MOCK_CLUB}
        </footer>
      </main>
    </div>
  );
}

// ============================================================
// COMPONENTES INTERNOS (puramente visuales)
// ============================================================

function DocumentSlot({
  previewUrl,
  placeholder,
}: {
  previewUrl: string | null;
  placeholder: string;
}) {
  return (
    <div className="relative aspect-[3/2] bg-gray-100 rounded-xl overflow-hidden border-2 border-dashed border-gray-300">
      {previewUrl ? (
        <img
          src={previewUrl}
          alt="documento"
          className="w-full h-full object-contain"
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 gap-2">
          <FileText className="w-10 h-10" />
          <span className="text-xs">{placeholder}</span>
        </div>
      )}
      {previewUrl && (
        <span className="absolute top-2 left-2 bg-green-500 text-white text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> Subido
        </span>
      )}
    </div>
  );
}

function Guarantee({
  icon: Icon,
  title,
  text,
}: {
  icon: typeof Lock;
  title: string;
  text: string;
}) {
  return (
    <div className="p-4 rounded-xl border border-gray-200 hover:border-primary-300 hover:bg-primary-50/50 transition-colors">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-primary-100 grid place-items-center flex-shrink-0">
          <Icon className="w-4 h-4 text-primary-700" />
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 text-sm mb-1">{title}</h4>
          <p className="text-xs text-gray-600 leading-relaxed">{text}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Vista previa estática del PDF que se firmará. Reproduce los textos
 * legales reales generados por `lib/pdf/consentPdf.ts` (templates
 * generateAdultConsentPdf / generateMinorConsentPdf).
 */
function PdfMockPreview({ audience }: { audience: Audience }) {
  return (
    <div className="rounded-lg border border-gray-300 bg-white shadow-inner overflow-hidden">
      <div className="bg-gray-100 border-b border-gray-200 px-4 py-2 flex items-center gap-2 text-xs text-gray-600">
        <FileText className="w-4 h-4" />
        <span className="font-mono">
          consentimiento-{audience === "minor" ? "menor" : "adulto"}.pdf
        </span>
        <span className="ml-auto text-amber-700 font-semibold">
          Vista previa · maqueta
        </span>
      </div>
      <div className="p-5 max-h-[60vh] overflow-y-auto">
        <pre className="whitespace-pre-wrap font-serif text-[12px] leading-relaxed text-gray-800">
          {audience === "adult" ? PERMISO_ADULT_TEXT : PERMISO_MINOR_TEXT}
        </pre>
        <div className="mt-6 pt-4 border-t border-dashed border-gray-300 text-center text-xs text-gray-400">
          [En el PDF real aquí va la firma manuscrita digital + foto de
          verificación + datos]
        </div>
      </div>
    </div>
  );
}

// ============================================================
// TEXTOS LEGALES REALES (idénticos al PDF generado en producción)
// Tomados verbatim de clubes_web/src/lib/pdf/consentPdf.ts
// Marcadores {{...}} se reemplazan por el sistema con datos del snapshot.
// ============================================================

const PERMISO_ADULT_TEXT = `CONSENTIMIENTO INFORMADO, AUTORIZACIONES Y RESPONSABILIDAD — PARTICIPANTE MAYOR DE EDAD

1. IDENTIFICACIÓN DEL PARTICIPANTE
Yo, {{NOMBRE_PARTICIPANTE}}, mayor de edad, identificado(a) con {{TIPO_DOC}} No. {{NUMERO_DOC}} expedida en {{LUGAR_EXPEDICION}}, con domicilio en {{DIRECCION}}, actuando en nombre propio y en pleno uso de mis facultades legales, MANIFIESTO que he sido debidamente informado(a) y otorgo mi consentimiento libre, previo, expreso e informado.

2. PARTICIPACIÓN EN LA ACTIVIDAD
Actividad: ${MOCK_EVENT_NAME}
Lugar: {{LUGAR_EVENTO}} - {{CIUDAD_EVENTO}}
Fechas: Del {{FECHA_INICIO}} al {{FECHA_FIN}}
Ciudad de salida: {{CIUDAD_SALIDA}}
Ciudad de regreso: {{CIUDAD_REGRESO}}
Entidad organizadora: Iglesia Adventista del Séptimo Día
Club / Dependencia: ${MOCK_CLUB}
Distrito: {{NOMBRE_DISTRITO}}

3. INFORMACIÓN MÉDICA Y CONDICIONES DE SALUD
EPS: {{EPS}}
Condiciones médicas: {{CONDICIONES_MEDICAS}}
Alergias: {{ALERGIAS}}
Medicamentos permanentes: {{MEDICAMENTOS}}
(Si declara no tener condiciones médicas relevantes, se imprime: "Declaro bajo gravedad de juramento que no presento condiciones médicas relevantes conocidas que impidan mi participación.")

4. AUTORIZACIÓN PARA ATENCIÓN MÉDICA DE URGENCIA
Autorizo a los organizadores y responsables de la actividad para que, en caso de emergencia o urgencia médica, se gestione y autorice la atención médica necesaria, incluyendo traslado a centros asistenciales. Los gastos derivados de dicha atención serán asumidos conforme a la cobertura de mi EPS, póliza o seguro correspondiente.

5. EXONERACIÓN DE RESPONSABILIDAD
Declaro que participo de manera voluntaria y consciente, asumiendo los riesgos normales derivados de la actividad, y exonero a la Iglesia Adventista del Séptimo Día, a sus directivos, organizadores, coordinadores y voluntarios, de responsabilidad civil por hechos derivados de mi conducta personal, negligencia o incumplimiento de las normas establecidas, salvo dolo o culpa grave conforme a la ley.

6. AUTORIZACIÓN DE USO DE IMAGEN
Autorizo a la Unión Colombiana del Norte, ${MOCK_ASSOCIATION} y al Club de Conquistadores ${MOCK_CLUB} (IASD), para el uso de mi imagen, voz y nombre, captados en fotografías, videos y producciones audiovisuales realizadas durante la actividad, con fines institucionales, educativos, informativos y pastorales, sin ánimo de lucro. Esta autorización se concede sin límite territorial ni temporal, conforme a la Ley 1581 de 2012, pudiendo ser revocada mediante solicitud escrita.

7. DECLARACIONES FINALES
Declaro que: (a) he leído y comprendido el contenido del presente documento; (b) la información suministrada es veraz; (c) firmo de manera libre, voluntaria y sin ningún tipo de presión.

8. FIRMA
[Recuadro de firma manuscrita digital del participante + nombre + documento + fecha de firma]`;

const PERMISO_MINOR_TEXT = `CONSENTIMIENTO INFORMADO Y AUTORIZACIONES PARA MENOR DE EDAD

1. IDENTIFICACIÓN DE LOS PADRES O ACUDIENTES
Yo, {{NOMBRE_PADRE_1}}, mayor de edad, identificado(a) con {{TIPO_DOC_1}} No. {{NUM_DOC_1}}, con domicilio en {{DIRECCION_1}}, en calidad de {{PARENTESCO_1}}, AUTORIZO la participación del menor.
(Si se agrega un segundo acudiente, se imprime un párrafo análogo iniciando con "Y yo, ...".)

Nombre del menor: {{NOMBRE_PARTICIPANTE}}
Tipo de documento: {{TIPO_DOC_PART}}
Número: {{NUM_DOC_PART}}
EPS: {{EPS}}

2. AUTORIZACIÓN DE PARTICIPACIÓN EN ACTIVIDAD
Manifestamos de manera libre, voluntaria e informada que autorizamos la participación de nuestro hijo(a) en la siguiente actividad institucional:
Actividad: ${MOCK_EVENT_NAME}
Lugar: {{LUGAR_EVENTO}} - {{CIUDAD_EVENTO}}
Fechas: Del {{FECHA_INICIO}} al {{FECHA_FIN}}
Ciudad de salida: {{CIUDAD_SALIDA}}
Ciudad de regreso: {{CIUDAD_REGRESO}}
Club organizador: Club de Conquistadores ${MOCK_CLUB}
Iglesia: Iglesia Adventista del Séptimo Día {{NOMBRE_IGLESIA}}
Distrito: {{NOMBRE_DISTRITO}}

(Si el evento tiene voto de junta registrado:)
Esta actividad cuenta con el conocimiento y aprobación de la directiva del Club y de la Junta de Iglesia, según voto de junta No. {{NUMERO_VOTO}} del {{FECHA_VOTO}}.

3. INFORMACIÓN MÉDICA DEL MENOR
EPS: {{EPS}}
Condiciones médicas: {{CONDICIONES_MEDICAS}}
Alergias: {{ALERGIAS}}
Medicamentos permanentes: {{MEDICAMENTOS}}
(Si los padres declaran que no hay condiciones médicas:)
"Declaramos bajo gravedad de juramento que el menor no presenta condiciones médicas relevantes conocidas."

4. AUTORIZACIÓN PARA ATENCIÓN MÉDICA DE URGENCIA
Autorizamos expresamente a los organizadores de la actividad, consejeros responsables y/o directores del club, para que en caso de urgencia o emergencia médica, nuestro hijo(a) reciba la atención médica inmediata que sea necesaria, incluyendo su traslado a un centro asistencial. La atención se realizará a través de la EPS indicada o del seguro correspondiente. Asumiremos los costos no cubiertos por el sistema de salud o póliza, si a ello hubiere lugar.

5. AUTORIZACIÓN DE USO DE IMAGEN Y MATERIAL AUDIOVISUAL
Autorizo de manera previa, expresa e informada a la Unión Colombiana del Norte, ${MOCK_ASSOCIATION} y al Club de Conquistadores ${MOCK_CLUB} (IASD), para el uso del derecho de imagen del menor, incluyendo fotografías y producciones audiovisuales captadas durante el desarrollo de la actividad, con fines institucionales, educativos, informativos y pastorales, sin ánimo de lucro. Esta autorización incluye su uso en medios impresos, digitales y plataformas oficiales de la Iglesia Adventista del Séptimo Día, dentro y fuera del territorio nacional. Conozco que esta autorización puede ser revocada en los términos establecidos por la ley, mediante solicitud escrita.

6. DECLARACIONES FINALES
Declaro bajo gravedad de juramento que: (a) la información suministrada es veraz y completa; (b) actúo en calidad de representante legal del menor; (c) he leído y comprendido el contenido del presente documento y lo firmo de manera libre y voluntaria.

7. FIRMAS
Por cada padre/acudiente registrado se incrusta:
  - Firma manuscrita digital
  - Foto de verificación (selfie con documento visible)
  - Nombre completo
  - Documento de identidad
Más al final:
  - Nombre del menor
  - Documento del menor
  - Fecha de firma`;
